// src/cockpit4/components/HermesNotificationCenter.tsx
// ============================================
// Hermes 主动提醒服务（Hermes 助手 P0-功能3）
//
// 设计理念：Hermes 主动服务，不是等人问才答。
//   1. 重要事项提醒（会议/截止日期/待办）
//   2. 异常情况主动报告（"今天收入下降了..."）
//   3. 每日晨间简报（聚合各模块数据自动推送）
//   4. 智能日程建议
//   5. 待办事项追踪和提醒
//   6. 周期报告自动生成和推送
//
// 数据来源（禁止假数据）：
//   - 提醒规则与通知记录：localStorage（key: xeaos_hermes_notifications）持久化
//   - 异常监控：通过 RuntimeManager.query 检测各模块 Runtime 真实状态
//     · RevenueEngine     → 收入下降预警
//     · Workflow/Agent    → 任务逾期提醒
//     · CRM/Sales         → 客户跟进提醒
//     · Content/Calendar  → 内容断更预警
//     · EnterpriseBrain   → 晨间简报聚合
//   - 无数据时显示"暂无通知 / 规则已配置"
//
// 提醒方式：界面弹窗 + 声音（可选，Web Audio API 合成，无外部资源）
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  BellRing,
  CheckCheck,
  CheckCircle2,
  Circle,
  AlertTriangle,
  AlertOctagon,
  Info,
  Sun,
  CalendarClock,
  ListTodo,
  FileBarChart,
  Settings2,
  Volume2,
  VolumeX,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Trash2,
  Pencil,
  X,
  Sparkles,
  Zap,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义
// ============================================

/** 通知类型 */
type NotificationType =
  | "reminder"
  | "alert"
  | "morning_brief"
  | "schedule"
  | "todo"
  | "report";

/** 通知级别 */
type NotificationLevel = "info" | "warning" | "error" | "success";

/** 通知状态 */
type NotificationStatus = "unread" | "read" | "handled";

/** 提醒规则 */
interface NotificationRule {
  id: string;
  name: string;
  type: NotificationType;
  enabled: boolean;
  condition: {
    metric: string; // 监控指标（如 revenue_drop, task_overdue）
    threshold?: number; // 阈值
    operator?: "gt" | "lt" | "eq";
  };
  level: NotificationLevel;
  createdAt: string;
}

/** 通知记录 */
interface Notification {
  id: string;
  ruleId?: string; // 关联规则
  type: NotificationType;
  level: NotificationLevel;
  title: string;
  content: string;
  suggestion?: string; // AI建议
  status: NotificationStatus;
  createdAt: string;
  handledAt?: string;
}

/** localStorage 持久化结构 */
interface NotificationStore {
  rules: NotificationRule[];
  notifications: Notification[];
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_hermes_notifications";

/** 内置提醒规则（首次初始化时写入 localStorage） */
const DEFAULT_RULES: NotificationRule[] = [
  {
    id: "rule_revenue_drop",
    name: "收入下降预警",
    type: "alert",
    enabled: true,
    condition: { metric: "revenue_drop", threshold: 20, operator: "gt" },
    level: "error",
    createdAt: new Date().toISOString(),
  },
  {
    id: "rule_task_overdue",
    name: "任务逾期提醒",
    type: "reminder",
    enabled: true,
    condition: { metric: "task_overdue" },
    level: "warning",
    createdAt: new Date().toISOString(),
  },
  {
    id: "rule_morning_brief",
    name: "每日晨间简报",
    type: "morning_brief",
    enabled: true,
    condition: { metric: "daily_morning" },
    level: "info",
    createdAt: new Date().toISOString(),
  },
  {
    id: "rule_followup_due",
    name: "客户跟进提醒",
    type: "reminder",
    enabled: true,
    condition: { metric: "followup_due" },
    level: "warning",
    createdAt: new Date().toISOString(),
  },
  {
    id: "rule_content_gap",
    name: "内容断更预警",
    type: "alert",
    enabled: true,
    condition: { metric: "content_gap", threshold: 3, operator: "gt" },
    level: "warning",
    createdAt: new Date().toISOString(),
  },
];

/** 通知类型 → 图标/颜色/标签映射 */
const TYPE_META: Record<
  NotificationType,
  { icon: typeof Bell; color: string; label: string }
> = {
  reminder: { icon: BellRing, color: "#A855F7", label: "提醒" },
  alert: { icon: AlertOctagon, color: "#EF4444", label: "告警" },
  morning_brief: { icon: Sun, color: "#F59E0B", label: "晨报" },
  schedule: { icon: CalendarClock, color: "#3B82F6", label: "日程" },
  todo: { icon: ListTodo, color: "#10B981", label: "待办" },
  report: { icon: FileBarChart, color: "#EC4899", label: "报告" },
};

/** 通知级别 → 颜色映射 */
const LEVEL_COLOR: Record<NotificationLevel, string> = {
  info: "#3B82F6",
  warning: "#F59E0B",
  error: "#EF4444",
  success: "#10B981",
};

/** 紫色主色调 */
const PRIMARY = "#A855F7";

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 读取 localStorage 全量状态 */
function loadStore(): NotificationStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // 首次初始化：写入默认规则
      const initial: NotificationStore = {
        rules: DEFAULT_RULES,
        notifications: [],
      };
      saveStore(initial);
      return initial;
    }
    const parsed = JSON.parse(raw) as NotificationStore;
    // 防御性校验
    if (!parsed || !Array.isArray(parsed.rules) || !Array.isArray(parsed.notifications)) {
      const initial: NotificationStore = { rules: DEFAULT_RULES, notifications: [] };
      saveStore(initial);
      return initial;
    }
    return parsed;
  } catch {
    return { rules: DEFAULT_RULES, notifications: [] };
  }
}

/** 写入 localStorage 全量状态 */
function saveStore(store: NotificationStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // 静默降级
  }
}

/** 格式化时间为相对时间描述 */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}

/** 判断 ISO 时间是否为今天 */
function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** Web Audio API 合成提示音（无外部资源） */
function playBeep(): void {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880; // A5 音调
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
    osc.onended = () => ctx.close();
  } catch {
    // 静默降级
  }
}

// ============================================
// 异常检测引擎（基于 RuntimeManager.query 真实数据）
// ============================================

interface DetectionResult {
  triggered: boolean;
  content: string;
  suggestion?: string;
}

/** 检测单个规则是否触发 */
async function detectRule(rule: NotificationRule): Promise<DetectionResult | null> {
  const { metric, threshold, operator } = rule.condition;

  // 比较函数
  const compare = (actual: number): boolean => {
    if (operator === "gt") return actual > (threshold ?? 0);
    if (operator === "lt") return actual < (threshold ?? 0);
    if (operator === "eq") return actual === (threshold ?? 0);
    return false;
  };

  switch (metric) {
    // 收入下降预警：查询 RevenueEngine
    case "revenue_drop": {
      if (!RuntimeManager.hasRuntime(RuntimeName.RevenueEngine)) return null;
      const resp = await RuntimeManager.query(RuntimeName.RevenueEngine, {
        type: "summary",
      }).catch(() => null);
      if (!resp?.success || !resp.data) return null;
      const data = resp.data as Record<string, unknown>;
      // 兼容多种字段命名
      const drop =
        (data.dailyDrop as number) ??
        (data.dropPercent as number) ??
        (data.revenueDrop as number) ??
        0;
      if (typeof drop !== "number" || !compare(drop)) return null;
      return {
        triggered: true,
        content: `今日收入较昨日下降 ${drop.toFixed(1)}%，已超过阈值 ${threshold}%。`,
        suggestion: "建议立即排查收入渠道，分析下降原因并制定补救措施。",
      };
    }

    // 任务逾期提醒：查询 Workflow / Agent
    case "task_overdue": {
      const runtimeName = RuntimeManager.hasRuntime(RuntimeName.Workflow)
        ? RuntimeName.Workflow
        : RuntimeManager.hasRuntime(RuntimeName.Agent)
        ? RuntimeName.Agent
        : null;
      if (!runtimeName) return null;
      const resp = await RuntimeManager.query(runtimeName, {
        type: "overdue_tasks",
      }).catch(() => null);
      if (!resp?.success || !resp.data) return null;
      const list = resp.data as unknown;
      const overdueCount = Array.isArray(list)
        ? list.length
        : (list as Record<string, unknown>)?.count as number ?? 0;
      if (overdueCount <= 0) return null;
      return {
        triggered: true,
        content: `当前有 ${overdueCount} 个任务已逾期，请尽快处理。`,
        suggestion: "建议优先处理逾期任务，必要时重新评估截止日期或资源分配。",
      };
    }

    // 客户跟进提醒：查询 CRM / Sales
    case "followup_due": {
      const runtimeName = RuntimeManager.hasRuntime(RuntimeName.CRM)
        ? RuntimeName.CRM
        : RuntimeManager.hasRuntime(RuntimeName.Sales)
        ? RuntimeName.Sales
        : null;
      if (!runtimeName) return null;
      const resp = await RuntimeManager.query(runtimeName, {
        type: "pending_followups",
      }).catch(() => null);
      if (!resp?.success || !resp.data) return null;
      const list = resp.data as unknown;
      const dueCount = Array.isArray(list)
        ? list.length
        : (list as Record<string, unknown>)?.count as number ?? 0;
      if (dueCount <= 0) return null;
      return {
        triggered: true,
        content: `有 ${dueCount} 位客户待跟进，跟进窗口即将关闭。`,
        suggestion: "建议按优先级排序，今日内完成高价值客户的首轮触达。",
      };
    }

    // 内容断更预警：查询 Content / ContentCalendar
    case "content_gap": {
      const runtimeName = RuntimeManager.hasRuntime(RuntimeName.ContentCalendar)
        ? RuntimeName.ContentCalendar
        : RuntimeManager.hasRuntime(RuntimeName.Content)
        ? RuntimeName.Content
        : null;
      if (!runtimeName) return null;
      const resp = await RuntimeManager.query(runtimeName, {
        type: "publish_gap_days",
      }).catch(() => null);
      if (!resp?.success || !resp.data) return null;
      const data = resp.data as Record<string, unknown>;
      const gapDays = (data.gapDays as number) ?? (data.days as number) ?? 0;
      if (typeof gapDays !== "number" || !compare(gapDays)) return null;
      return {
        triggered: true,
        content: `内容已断更 ${gapDays} 天，超过阈值 ${threshold} 天。`,
        suggestion: "建议立即排期发布，保持内容连续性以维护平台权重。",
      };
    }

    // 晨间简报：由专用函数生成，此处不触发
    case "daily_morning":
      return null;

    default:
      return null;
  }
}

/** 批量检测所有启用的规则，返回新生成的通知（去重：同规则当天不重复） */
async function runDetection(
  rules: NotificationRule[],
  existing: Notification[]
): Promise<Notification[]> {
  const enabledRules = rules.filter((r) => r.enabled);
  const todayExisting = new Set(
    existing
      .filter((n) => n.ruleId && isToday(n.createdAt))
      .map((n) => n.ruleId as string)
  );

  const results: Notification[] = [];
  for (const rule of enabledRules) {
    // 同规则当天已产生通知则跳过，避免重复告警
    if (todayExisting.has(rule.id)) continue;
    try {
      const res = await detectRule(rule);
      if (res?.triggered) {
        results.push({
          id: genId(),
          ruleId: rule.id,
          type: rule.type,
          level: rule.level,
          title: rule.name,
          content: res.content,
          suggestion: res.suggestion,
          status: "unread",
          createdAt: new Date().toISOString(),
        });
      }
    } catch {
      // 单条规则检测失败不影响其他规则
    }
  }
  return results;
}

// ============================================
// 晨间简报生成（聚合各模块 Runtime 真实数据）
// ============================================

interface BriefingModule {
  name: string;
  status: "ok" | "warn" | "error" | "unknown";
  summary: string;
}

/** 聚合各模块 Runtime 状态生成晨间简报通知 */
async function buildMorningBrief(existing: Notification[]): Promise<Notification | null> {
  // 当天已生成过晨报则跳过
  const todayBrief = existing.find(
    (n) => n.type === "morning_brief" && isToday(n.createdAt)
  );
  if (todayBrief) return null;

  const modules: BriefingModule[] = [];
  const status = RuntimeManager.getStatusSummary();

  // 1. 收集各 Runtime 健康状态
  const checkedRuntimes: RuntimeName[] = [
    RuntimeName.RevenueEngine,
    RuntimeName.CRM,
    RuntimeName.Content,
    RuntimeName.Workflow,
    RuntimeName.EnterpriseBrain,
    RuntimeName.Sales,
  ];

  for (const name of checkedRuntimes) {
    if (!RuntimeManager.hasRuntime(name)) continue;
    const rtStatus = status.status[name];
    const health = status.health[name];
    let moduleStatus: BriefingModule["status"] = "unknown";
    if (rtStatus === "ready" && health === "healthy") moduleStatus = "ok";
    else if (rtStatus === "error" || health === "unhealthy") moduleStatus = "error";
    else if (rtStatus === "loading" || health === "degraded") moduleStatus = "warn";

    modules.push({
      name: String(name),
      status: moduleStatus,
      summary: `状态：${rtStatus} / 健康：${health}`,
    });
  }

  // 2. 尝试拉取 EnterpriseBrain 今日重点
  let focusText = "";
  try {
    if (RuntimeManager.hasRuntime(RuntimeName.EnterpriseBrain)) {
      const date = new Date().toISOString().slice(0, 10);
      const resp = await RuntimeManager.execute(
        RuntimeName.EnterpriseBrain,
        "get_today_focus",
        { date }
      ).catch(() => null);
      if (resp?.success && resp.data) {
        const data = resp.data as Record<string, unknown>;
        const priorities = data.priorities as unknown[];
        if (Array.isArray(priorities) && priorities.length > 0) {
          focusText = `今日重点事项 ${priorities.length} 项，建议优先处理第一项。`;
        }
      }
    }
  } catch {
    // 静默降级
  }

  // 3. 统计异常模块数
  const errorCount = modules.filter((m) => m.status === "error").length;
  const warnCount = modules.filter((m) => m.status === "warn").length;

  const contentParts: string[] = [];
  contentParts.push(`监测 ${modules.length} 个模块：${errorCount} 异常、${warnCount} 降级、${modules.length - errorCount - warnCount} 正常。`);
  if (focusText) contentParts.push(focusText);
  if (errorCount > 0) {
    const errorNames = modules.filter((m) => m.status === "error").map((m) => m.name);
    contentParts.push(`异常模块：${errorNames.join("、")}`);
  }

  // 4. 若所有模块均未注册，则不生成（避免假数据）
  if (modules.length === 0) return null;

  return {
    id: genId(),
    type: "morning_brief",
    level: errorCount > 0 ? "warning" : "info",
    title: `每日晨间简报 · ${new Date().toLocaleDateString("zh-CN")}`,
    content: contentParts.join(" "),
    suggestion:
      errorCount > 0
        ? "建议优先排查异常模块，恢复后再次确认。"
        : "各模块运行正常，建议按今日重点事项推进工作。",
    status: "unread",
    createdAt: new Date().toISOString(),
  };
}

// ============================================
// 主组件
// ============================================

export default function HermesNotificationCenter() {
  const [store, setStore] = useState<NotificationStore>(() => loadStore());
  const [detecting, setDetecting] = useState(false);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [expandedRules, setExpandedRules] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // 弹窗通知队列（最新一条未读）
  const [popup, setPopup] = useState<Notification | null>(null);
  const lastPopupIdRef = useRef<string | null>(null);

  // ============================================
  // 持久化：每次 store 变更都写回 localStorage
  // ============================================
  useEffect(() => {
    saveStore(store);
  }, [store]);

  // ============================================
  // 弹窗提醒：当出现新的未读通知时弹出
  // ============================================
  useEffect(() => {
    const unread = store.notifications
      .filter((n) => n.status === "unread")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const latest = unread[0];
    if (latest && latest.id !== lastPopupIdRef.current) {
      lastPopupIdRef.current = latest.id;
      setPopup(latest);
      if (soundOn) playBeep();
      // 6 秒后自动关闭弹窗
      const timer = setTimeout(() => setPopup(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [store.notifications, soundOn]);

  // ============================================
  // 通知摘要统计
  // ============================================
  const summary = useMemo(() => {
    const unread = store.notifications.filter((n) => n.status === "unread").length;
    const today = store.notifications.filter((n) => isToday(n.createdAt)).length;
    const pending = store.notifications.filter((n) => n.status !== "handled").length;
    return { unread, today, pending };
  }, [store.notifications]);

  // ============================================
  // 通知列表：按时间倒序 + 未读优先
  // ============================================
  const sortedNotifications = useMemo(() => {
    const statusWeight: Record<NotificationStatus, number> = {
      unread: 0,
      read: 1,
      handled: 2,
    };
    return [...store.notifications].sort((a, b) => {
      const w = statusWeight[a.status] - statusWeight[b.status];
      if (w !== 0) return w;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [store.notifications]);

  // ============================================
  // 操作：标记单条已读
  // ============================================
  const markRead = useCallback((id: string) => {
    setStore((prev) => ({
      ...prev,
      notifications: prev.notifications.map((n) =>
        n.id === id && n.status === "unread"
          ? { ...n, status: "read" }
          : n
      ),
    }));
  }, []);

  // ============================================
  // 操作：标记单条已处理
  // ============================================
  const markHandled = useCallback((id: string) => {
    setStore((prev) => ({
      ...prev,
      notifications: prev.notifications.map((n) =>
        n.id === id
          ? { ...n, status: "handled", handledAt: new Date().toISOString() }
          : n
      ),
    }));
  }, []);

  // ============================================
  // 操作：标记全部已读
  // ============================================
  const markAllRead = useCallback(() => {
    setStore((prev) => ({
      ...prev,
      notifications: prev.notifications.map((n) =>
        n.status === "unread" ? { ...n, status: "read" } : n
      ),
    }));
    setToast("已将全部通知标记为已读");
  }, []);

  // ============================================
  // 操作：删除单条通知
  // ============================================
  const removeNotification = useCallback((id: string) => {
    setStore((prev) => ({
      ...prev,
      notifications: prev.notifications.filter((n) => n.id !== id),
    }));
  }, []);

  // ============================================
  // 操作：清空已处理通知
  // ============================================
  const clearHandled = useCallback(() => {
    setStore((prev) => ({
      ...prev,
      notifications: prev.notifications.filter((n) => n.status !== "handled"),
    }));
    setToast("已清空已处理通知");
  }, []);

  // ============================================
  // 操作：切换规则启用状态
  // ============================================
  const toggleRule = useCallback((ruleId: string) => {
    setStore((prev) => ({
      ...prev,
      rules: prev.rules.map((r) =>
        r.id === ruleId ? { ...r, enabled: !r.enabled } : r
      ),
    }));
  }, []);

  // ============================================
  // 操作：更新规则阈值
  // ============================================
  const updateRuleThreshold = useCallback(
    (ruleId: string, threshold: number) => {
      setStore((prev) => ({
        ...prev,
        rules: prev.rules.map((r) =>
          r.id === ruleId
            ? { ...r, condition: { ...r.condition, threshold } }
            : r
        ),
      }));
    },
    []
  );

  // ============================================
  // 触发异常检测
  // ============================================
  const handleDetect = useCallback(async () => {
    setDetecting(true);
    setToast(null);
    try {
      const newOnes = await runDetection(store.rules, store.notifications);
      if (newOnes.length > 0) {
        setStore((prev) => ({
          ...prev,
          notifications: [...newOnes, ...prev.notifications],
        }));
        setToast(`检测完成，新增 ${newOnes.length} 条通知`);
      } else {
        setToast("检测完成，暂无新增异常");
      }
    } catch (err) {
      setToast(`检测失败：${(err as Error).message}`);
    } finally {
      setDetecting(false);
    }
  }, [store.rules, store.notifications]);

  // ============================================
  // 生成晨间简报
  // ============================================
  const handleMorningBrief = useCallback(async () => {
    setBriefingLoading(true);
    setToast(null);
    try {
      const brief = await buildMorningBrief(store.notifications);
      if (brief) {
        setStore((prev) => ({
          ...prev,
          notifications: [brief, ...prev.notifications],
        }));
        setToast("晨间简报已生成");
      } else {
        setToast("今日已生成晨间简报，或无可用模块数据");
      }
    } catch (err) {
      setToast(`晨间简报生成失败：${(err as Error).message}`);
    } finally {
      setBriefingLoading(false);
    }
  }, [store.notifications]);

  // ============================================
  // Toast 自动消失
  // ============================================
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ============================================
  // 渲染：通知级别图标
  // ============================================
  const renderLevelIcon = (level: NotificationLevel) => {
    const color = LEVEL_COLOR[level];
    switch (level) {
      case "error":
        return <AlertOctagon size={12} style={{ color }} />;
      case "warning":
        return <AlertTriangle size={12} style={{ color }} />;
      case "success":
        return <CheckCircle2 size={12} style={{ color }} />;
      case "info":
      default:
        return <Info size={12} style={{ color }} />;
    }
  };

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带（紫色主色调） */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <BellRing size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              Hermes 主动提醒服务
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Proactive Notification Center
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 声音开关 */}
          <button
            onClick={() => setSoundOn((v) => !v)}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors"
            title={soundOn ? "关闭声音提醒" : "开启声音提醒"}
          >
            {soundOn ? (
              <Volume2 size={11} className="text-purple-400" />
            ) : (
              <VolumeX size={11} className="text-gray-500" />
            )}
          </button>

          {/* 晨间简报按钮 */}
          <button
            onClick={handleMorningBrief}
            disabled={briefingLoading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
            title="生成每日晨间简报（聚合各模块 Runtime 数据）"
          >
            {briefingLoading ? (
              <RefreshCw size={11} className="text-purple-400 animate-spin" />
            ) : (
              <Sun size={11} className="text-purple-400" />
            )}
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              {briefingLoading ? "生成中..." : "晨报"}
            </span>
          </button>

          {/* 异常检测按钮 */}
          <button
            onClick={handleDetect}
            disabled={detecting}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
            title="主动检测各模块异常并生成告警"
          >
            {detecting ? (
              <RefreshCw size={11} className="text-purple-400 animate-spin" />
            ) : (
              <Zap size={11} className="text-purple-400" />
            )}
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              {detecting ? "检测中..." : "检测"}
            </span>
          </button>
        </div>
      </div>

      {/* 顶部通知摘要 */}
      <div className="px-4 py-3 border-b border-white/[0.04]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            {/* 未读数 */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.06] min-w-[68px]">
              <Bell size={11} className="text-purple-400" />
              <div className="flex flex-col leading-none">
                <span className="text-[12px] font-mono font-bold text-white">
                  {summary.unread}
                </span>
                <span className="text-[8px] font-mono text-gray-500 uppercase mt-0.5">
                  未读
                </span>
              </div>
            </div>
            {/* 今日通知数 */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.06] min-w-[68px]">
              <CalendarClock size={11} className="text-blue-400" />
              <div className="flex flex-col leading-none">
                <span className="text-[12px] font-mono font-bold text-white">
                  {summary.today}
                </span>
                <span className="text-[8px] font-mono text-gray-500 uppercase mt-0.5">
                  今日
                </span>
              </div>
            </div>
            {/* 待处理数 */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.06] min-w-[68px]">
              <Circle size={11} className="text-amber-400" />
              <div className="flex flex-col leading-none">
                <span className="text-[12px] font-mono font-bold text-white">
                  {summary.pending}
                </span>
                <span className="text-[8px] font-mono text-gray-500 uppercase mt-0.5">
                  待处理
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {/* 标记全部已读 */}
            <button
              onClick={markAllRead}
              disabled={summary.unread === 0}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors disabled:opacity-40"
              title="将所有未读通知标记为已读"
            >
              <CheckCheck size={10} className="text-purple-300" />
              <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
                全部已读
              </span>
            </button>
            {/* 清空已处理 */}
            <button
              onClick={clearHandled}
              disabled={!store.notifications.some((n) => n.status === "handled")}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors disabled:opacity-40"
              title="清空已处理的通知记录"
            >
              <Trash2 size={10} className="text-gray-400" />
              <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                清空
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Toast 提示 */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="px-4 py-2">
              <div className="px-3 py-2 rounded-md border border-purple-500/30 bg-purple-500/[0.06] text-[10px] font-mono text-purple-300">
                {toast}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 中部：通知列表 */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            通知列表
          </span>
          <span className="text-[9px] font-mono text-gray-600">
            共 {store.notifications.length} 条
          </span>
        </div>

        {sortedNotifications.length === 0 ? (
          <div className="py-10 text-center">
            <Bell size={28} className="mx-auto text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">暂无通知</p>
            <p className="text-[9px] font-mono text-gray-600 mt-1">
              点击「检测」主动扫描异常，或点击「晨报」生成每日简报
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
            {sortedNotifications.map((n) => {
              const typeMeta = TYPE_META[n.type];
              const TypeIcon = typeMeta.icon;
              const levelColor = LEVEL_COLOR[n.level];
              const isUnread = n.status === "unread";
              const isHandled = n.status === "handled";

              return (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`rounded-md border p-2.5 transition-colors ${
                    isUnread
                      ? "border-purple-500/30 bg-purple-500/[0.04]"
                      : "border-white/[0.06] bg-white/[0.02]"
                  } ${isHandled ? "opacity-55" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    {/* 类型图标 */}
                    <div
                      className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center mt-0.5"
                      style={{ background: `${typeMeta.color}15` }}
                    >
                      <TypeIcon size={11} style={{ color: typeMeta.color }} />
                    </div>

                    {/* 内容 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span className="text-[11px] font-semibold text-white truncate">
                          {n.title}
                        </span>
                        {/* 级别标签 */}
                        <span
                          className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{ background: `${levelColor}15`, color: levelColor }}
                        >
                          {renderLevelIcon(n.level)}
                          {n.level}
                        </span>
                        {/* 类型标签 */}
                        <span
                          className="px-1 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{
                            background: `${typeMeta.color}10`,
                            color: typeMeta.color,
                          }}
                        >
                          {typeMeta.label}
                        </span>
                        {/* 状态标签 */}
                        {isHandled && (
                          <span className="px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-emerald-500/10 text-emerald-400">
                            已处理
                          </span>
                        )}
                      </div>

                      <p className="text-[10px] text-gray-300 leading-snug mb-1">
                        {n.content}
                      </p>

                      {/* AI 建议 */}
                      {n.suggestion && (
                        <div className="flex items-start gap-1 mb-1">
                          <Sparkles size={9} className="text-purple-400 mt-0.5 flex-shrink-0" />
                          <p className="text-[9px] font-mono text-purple-300/90 leading-snug">
                            {n.suggestion}
                          </p>
                        </div>
                      )}

                      {/* 底部操作行 */}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[8px] font-mono text-gray-600">
                          {formatRelativeTime(n.createdAt)}
                          {n.handledAt && ` · 已处理于 ${formatRelativeTime(n.handledAt)}`}
                        </span>
                        <div className="flex items-center gap-1">
                          {isUnread && (
                            <button
                              onClick={() => markRead(n.id)}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
                              title="标记为已读"
                            >
                              <CheckCircle2 size={9} />
                              已读
                            </button>
                          )}
                          {!isHandled && (
                            <button
                              onClick={() => markHandled(n.id)}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                              title="标记为已处理"
                            >
                              <CheckCheck size={9} />
                              已处理
                            </button>
                          )}
                          <button
                            onClick={() => removeNotification(n.id)}
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                            title="删除该通知"
                          >
                            <Trash2 size={9} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* 底部：提醒规则管理 */}
      <div className="border-t border-white/[0.04]">
        <button
          onClick={() => setExpandedRules((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-center gap-2">
            <Settings2 size={11} className="text-purple-400" />
            <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
              提醒规则管理
            </span>
            <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-purple-500/10 text-purple-300">
              {store.rules.filter((r) => r.enabled).length}/{store.rules.length}
            </span>
          </div>
          {expandedRules ? (
            <ChevronDown size={12} className="text-gray-500" />
          ) : (
            <ChevronRight size={12} className="text-gray-500" />
          )}
        </button>

        <AnimatePresence>
          {expandedRules && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              {store.rules.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Settings2 size={20} className="mx-auto text-gray-700 mb-1" />
                  <p className="text-[11px] font-mono text-gray-500">规则已配置</p>
                </div>
              ) : (
                <div className="px-4 pb-4 space-y-1.5">
                  {store.rules.map((rule) => {
                    const typeMeta = TYPE_META[rule.type];
                    const TypeIcon = typeMeta.icon;
                    const isEditing = editingRuleId === rule.id;

                    return (
                      <div
                        key={rule.id}
                        className={`rounded-md border p-2.5 transition-colors ${
                          rule.enabled
                            ? "border-purple-500/20 bg-purple-500/[0.03]"
                            : "border-white/[0.04] bg-white/[0.01] opacity-60"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <TypeIcon
                              size={11}
                              style={{ color: typeMeta.color }}
                              className="flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[11px] font-semibold text-white truncate">
                                  {rule.name}
                                </span>
                                <span
                                  className="px-1 py-0.5 rounded text-[7px] font-mono uppercase"
                                  style={{
                                    background: `${LEVEL_COLOR[rule.level]}15`,
                                    color: LEVEL_COLOR[rule.level],
                                  }}
                                >
                                  {rule.level}
                                </span>
                              </div>
                              <p className="text-[8px] font-mono text-gray-500 mt-0.5 truncate">
                                metric: {rule.condition.metric}
                                {rule.condition.threshold !== undefined &&
                                  ` · ${rule.condition.operator ?? "gt"} ${rule.condition.threshold}`}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* 编辑阈值 */}
                            {rule.condition.threshold !== undefined && (
                              <button
                                onClick={() =>
                                  setEditingRuleId(isEditing ? null : rule.id)
                                }
                                className="p-1 rounded text-gray-500 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
                                title="编辑阈值"
                              >
                                <Pencil size={9} />
                              </button>
                            )}
                            {/* 启用/禁用开关 */}
                            <button
                              onClick={() => toggleRule(rule.id)}
                              className={`relative w-7 h-3.5 rounded-full transition-colors ${
                                rule.enabled ? "bg-purple-500/40" : "bg-white/[0.08]"
                              }`}
                              title={rule.enabled ? "禁用规则" : "启用规则"}
                            >
                              <span
                                className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${
                                  rule.enabled ? "left-3.5" : "left-0.5"
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* 阈值编辑器 */}
                        <AnimatePresence>
                          {isEditing && rule.condition.threshold !== undefined && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-2 pt-2 border-t border-white/[0.04] flex items-center gap-2">
                                <span className="text-[9px] font-mono text-gray-500">
                                  阈值：
                                </span>
                                <input
                                  type="number"
                                  value={rule.condition.threshold}
                                  onChange={(e) =>
                                    updateRuleThreshold(
                                      rule.id,
                                      Number(e.target.value) || 0
                                    )
                                  }
                                  className="w-20 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
                                />
                                <span className="text-[9px] font-mono text-gray-500">
                                  操作：
                                  {rule.condition.operator ?? "gt"}
                                </span>
                                <button
                                  onClick={() => setEditingRuleId(null)}
                                  className="ml-auto px-2 py-0.5 rounded text-[9px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
                                >
                                  完成
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 弹窗提醒（最新未读通知） */}
      <AnimatePresence>
        {popup && (
          <motion.div
            key={popup.id}
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96 }}
            className="absolute top-14 left-4 right-4 z-20 rounded-lg border border-purple-500/40 bg-[#1a1228]/95 backdrop-blur-xl shadow-2xl shadow-purple-500/20 overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-400/60 to-transparent" />
            <div className="flex items-start gap-2 p-3">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
                style={{ background: `${LEVEL_COLOR[popup.level]}20` }}
              >
                {renderLevelIcon(popup.level)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[11px] font-bold text-white truncate">
                    {popup.title}
                  </span>
                  <span
                    className="px-1 py-0.5 rounded text-[7px] font-mono uppercase"
                    style={{
                      background: `${LEVEL_COLOR[popup.level]}20`,
                      color: LEVEL_COLOR[popup.level],
                    }}
                  >
                    {popup.level}
                  </span>
                </div>
                <p className="text-[10px] text-gray-300 leading-snug">
                  {popup.content}
                </p>
                {popup.suggestion && (
                  <p className="text-[9px] font-mono text-purple-300 mt-1 leading-snug">
                    💡 {popup.suggestion}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => {
                      markRead(popup.id);
                      setPopup(null);
                    }}
                    className="px-2 py-0.5 rounded text-[9px] font-mono text-purple-300 bg-purple-500/15 hover:bg-purple-500/25 transition-colors"
                  >
                    知道了
                  </button>
                  <button
                    onClick={() => {
                      markHandled(popup.id);
                      setPopup(null);
                    }}
                    className="px-2 py-0.5 rounded text-[9px] font-mono text-emerald-300 bg-emerald-500/15 hover:bg-emerald-500/25 transition-colors"
                  >
                    立即处理
                  </button>
                </div>
              </div>
              <button
                onClick={() => setPopup(null)}
                className="p-0.5 rounded text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors flex-shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
